import { useParams } from 'react-router-dom'
import { useContext, useRef, useState, useEffect } from 'react'
import { APIContext } from 'context/APIContext'
import { useGetExercises } from 'redux/customHooks/useGetExercises'

import FullscreenLoading from 'components/Loading/FullscreenLoading'
import ScreenFrame from 'components/Frame/ScreenFrame'
import ProgramEditorCanvasControlsOverlay from 'components/ProgramEditor/ProgramEditorCanvasControlsOverlay'
import WorkoutCanvasTab from 'components/ProgramEditor/WorkoutCanvasTab'
import VolumeCanvasTab from 'components/ProgramEditor/VolumeCanvasTab'
import MesoVolumeCanvasTab from 'components/ProgramEditor/MesoVolumeCanvasTab'
import AssessmentCanvasTab from 'components/ProgramEditor/AssessmentCanvasTab'
import ExerciseCanvasTab from 'components/ProgramEditor/ExerciseCanvasTab'

function isJsonObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

const programEditorTabs = [
    {
        key: 'workouts',
        label: 'Workouts'
    },
    {
        key: 'volume',
        label: 'Volume'
    },
    {
        key: 'mesoVolumes',
        label: 'Meso Volumes'
    },
    {
        key: 'assessment',
        label: 'Assessment'
    },
    {
        key: 'exercises',
        label: 'Exercises'
    }
]

const GRID_SIZE = 24

export default function ProgramCanvasView() {

    const { urlProgramId } = useParams()

    const { GetProgramCanvasData } = useContext(APIContext)

    const { exerciseHashMap } = useGetExercises()

    const viewportRef = useRef(null)
    const isPanningRef = useRef(false)
    const lastPointerRef = useRef({ x: 0, y: 0 })

    const [selectedProgramEditorTab, setSelectedProgramEditorTab] = useState(programEditorTabs[0].key)
    const [showEquipmentFilteredExercises, setShowEquipmentFilteredExercises] = useState(false)
    const [highlightInjuryOverrides, setHighlightInjuryOverrides] = useState(false)
    const [transform, setTransform] = useState({ x: 0, y: 0, zoom: 1 })
    const [programEditorData, setProgramEditorData] = useState(null)

    const InitializeProgramCanvasData = async () => {
        setProgramEditorData(null)
        const programEditorData = await GetProgramCanvasData(urlProgramId)
        setProgramEditorData(programEditorData)
    }

    const onPointerDown = (event) => {
        if (event.button !== 0 && event.button !== 1) return

        isPanningRef.current = true
        lastPointerRef.current = { x: event.clientX, y: event.clientY }
        event.currentTarget.setPointerCapture(event.pointerId)
    }

    const onPointerMove = (event) => {
        if (!isPanningRef.current) return

        const dx = event.clientX - lastPointerRef.current.x
        const dy = event.clientY - lastPointerRef.current.y
        lastPointerRef.current = { x: event.clientX, y: event.clientY }

        setTransform((current) => ({
            ...current,
            x: current.x + dx,
            y: current.y + dy,
        }))
    }

    const onPointerUp = () => {
        isPanningRef.current = false
    }

    const assessmentTemplatesJSON = programEditorData?.programMetadata?.assessmentTemplatesJSON
    const showAssessmentTab = isJsonObject(assessmentTemplatesJSON)
    const isProgramInstance = programEditorData?.programMetadata?.type === 'instance'
    const visibleProgramEditorTabs = isProgramInstance
        ? programEditorTabs.filter((tab) => tab.key === 'exercises')
        : showAssessmentTab
            ? programEditorTabs
            : programEditorTabs.filter((tab) => tab.key !== 'assessment')
    const activeProgramEditorTab = isProgramInstance ? 'exercises' : selectedProgramEditorTab

    useEffect(() => { InitializeProgramCanvasData() }, [urlProgramId])

    useEffect(() => {
        if (programEditorData === null) return
        if (programEditorData.programMetadata?.type === 'instance') {
            if (selectedProgramEditorTab !== 'exercises') {
                setSelectedProgramEditorTab('exercises')
            }
            return
        }
        if (!showAssessmentTab && selectedProgramEditorTab === 'assessment') {
            setSelectedProgramEditorTab('workouts')
        }
    }, [programEditorData, showAssessmentTab, selectedProgramEditorTab])

    if (
        Object.keys(exerciseHashMap).length === 0
        || programEditorData === null
    ) {
        return <FullscreenLoading />
    }

    return (
        <ScreenFrame
            limitWidth={false}
            alwaysHideResumeWorkoutHeader={true}
            contentClassName="!space-y-0 -m-6 2xl:-m-8"
        >
            <div
                ref={viewportRef}
                className="relative w-full h-[calc(100dvh-12px)] overflow-hidden touch-none select-none cursor-grab active:cursor-grabbing bg-[#f4f4f5]"
                style={{
                    backgroundImage: 'radial-gradient(circle, #d4d4d8 1px, transparent 1px)',
                    backgroundSize: `${GRID_SIZE * transform.zoom}px ${GRID_SIZE * transform.zoom}px`,
                    backgroundPosition: `${transform.x}px ${transform.y}px`,
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            >
                <ProgramEditorCanvasControlsOverlay
                    viewportRef={viewportRef}
                    transform={transform}
                    setTransform={setTransform}
                    programEditorTabs={visibleProgramEditorTabs}
                    selectedProgramEditorTab={activeProgramEditorTab}
                    setSelectedProgramEditorTab={setSelectedProgramEditorTab}
                />

                <div
                    className="absolute left-0 top-0 origin-top-left will-change-transform"
                    style={{
                        transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.zoom})`,
                    }}
                >
                    {activeProgramEditorTab === 'workouts' && (
                        <WorkoutCanvasTab
                            viewportRef={viewportRef}
                            setTransform={setTransform}
                            workoutConfigs={programEditorData?.workoutConfigs ?? []}
                        />
                    )}

                    {activeProgramEditorTab === 'volume' && (
                        <VolumeCanvasTab
                            viewportRef={viewportRef}
                            setTransform={setTransform}
                            programMetadata={programEditorData?.programMetadata ?? {}}
                            programVolumeConfigs={programEditorData?.programVolumeConfigs ?? []}
                            mesoVolumeConfigs={programEditorData?.mesoVolumeConfigs ?? []}
                        />
                    )}

                    {activeProgramEditorTab === 'mesoVolumes' && (
                        <MesoVolumeCanvasTab
                            viewportRef={viewportRef}
                            setTransform={setTransform}
                            programMetadata={programEditorData?.programMetadata ?? {}}
                            mesoVolumeConfigs={programEditorData?.mesoVolumeConfigs ?? []}
                        />
                    )}

                    {activeProgramEditorTab === 'assessment' && showAssessmentTab && (
                        <AssessmentCanvasTab
                            viewportRef={viewportRef}
                            setTransform={setTransform}
                            exerciseHashMap={exerciseHashMap}
                            programLevelHashMap={programEditorData?.programMetadata?.programLevelHashMap ?? {}}
                            assessmentTemplatesJSON={assessmentTemplatesJSON}
                            assessmentApplicationJSON={programEditorData?.programMetadata?.assessmentApplicationJSON ?? []}
                            exerciseBlocks={programEditorData?.exerciseBlocks ?? []}
                        />
                    )}

                    {activeProgramEditorTab === 'exercises' && (
                        <ExerciseCanvasTab
                            viewportRef={viewportRef}
                            setTransform={setTransform}
                            exerciseHashMap={exerciseHashMap}
                            programMetadata={programEditorData?.programMetadata ?? {}}
                            workoutConfigs={programEditorData?.workoutConfigs ?? []}
                            exerciseBlocks={programEditorData?.exerciseBlocks ?? []}
                            showEquipmentFilteredExercises={showEquipmentFilteredExercises}
                            setShowEquipmentFilteredExercises={setShowEquipmentFilteredExercises}
                            highlightInjuryOverrides={highlightInjuryOverrides}
                            setHighlightInjuryOverrides={setHighlightInjuryOverrides}
                            showHighlightInjuryOverridesSwitch={(programEditorData?.userActiveInjury ?? []).length > 0}
                        />
                    )}
                </div>
            </div>
        </ScreenFrame>
    )
}
